import { create } from 'zustand';
import type { UserRole, UserSummary } from '@aslgo/shared';
import {
  readSession as readStoredSession,
  writeSession as writeStoredSession,
  clearSession as clearStoredSession,
  getValidAccessToken,
  type StoredSession,
} from '../lib/session.js';
import { identifyUser, resetUser } from '../lib/posthog.js';
import { signInErrorMessage } from '../lib/authErrors.js';

export type View = 'login' | 'login-only' | 'forgot' | 'reset' | 'lobby' | 'incoming' | 'call' | 'rate' | 'loopback' | 'register-learner' | 'register-guide' | 'support' | 'profile';

export interface AppState {
  view: View;
  /** Auth uuid of the signed-in user — the identity used for LiveKit,
   *  room naming, signalling and role lookups. */
  userId: string | null;
  /** Display name of the signed-in user. Presentation only. */
  displayName: string | null;
  /** Email of the signed-in user. Mirrors `profiles.email`. Used for the
   *  current-password re-auth in the profile page's password change. */
  email: string | null;
  /** `profiles.registration_status` (e.g. 'pending_review' | 'active'); learners can now be 'pending_review' too (flagged full name), not just guides; null when unset. */
  registrationStatus: string | null;
  /** `guide_profiles.application_stage` for guides mid-onboarding; null for learners / non-guides. */
  applicationStage: string | null;
  /** Admin "changes requested" note for the current stage, or null. */
  stageFeedback: string | null;
  role: UserRole | null;
  /** Auth uuid of the caller whose invite is ringing — a key. Views resolve the
   *  name off `users`. */
  incomingFrom: string | null;
  /** Display name the caller carried on their LiveKit token — a label, and a
   *  fresher one than the roster, which only reloads at sign-in/bootstrap. Null
   *  when the invite arrived without a participant object; IncomingView then
   *  falls back to the roster. */
  incomingFromName: string | null;
  /** Session length (minutes) the incoming caller requested; null when no
   *  invite is pending or the caller sent none. */
  incomingDurationMinutes: number | null;
  /** What the incoming caller wants to work on; null when no invite is pending
   *  or the caller sent none. Already sanitized when it reaches the store. */
  incomingTopic: string | null;
  /** Session length (minutes) requested for the call currently in progress —
   *  the learner's chosen duration, or the guide's accepted invite duration.
   *  Drives the in-call countdown (`useSessionTimer`); null when no call. */
  callDurationMinutes: number | null;
  /** Live remaining seconds of the in-call session countdown, mirrored from
   *  CallView's timer so the top bar can display it; null when not in a call. */
  sessionCountdownSeconds: number | null;
  /** Auth uuid of the peer in the call in progress — a key (it names the room
   *  and addresses signals). */
  callPeer: string | null;
  /** Auth uuid whose loopback self-test room we're in — a key. */
  loopbackOf: string | null;
  /** Lobby roster from `public_profiles`. */
  users: UserSummary[];
  /** Purchased minute balance for the signed-in learner. Always 0 for guides. */
  sessionMinutes: number;
  /** Set when a call connects; cleared on rating submit/skip. RatingView reads this to know which session to rate. */
  lastSessionId: number | null;
  /** Peer DISPLAY NAME captured at end-of-call so RatingView can show "Rate
   *  your session with X" even after callPeer is cleared. A label, resolved
   *  from the roster when the call ends — never a uuid. */
  lastCallPeer: string | null;
  /** Guide availability toggle. NOT persisted → resets to false (Standby) on
   *  every fresh page load. Drives the ready attribute on the lobby participant. */
  guideReady: boolean;
  /** The iOS app's "Buy minutes" hand-off landed here (`/?buy=1` after the
   *  magic-link sign-in). Set once per page load for a signed-in learner;
   *  AddMinutesPanel consumes it (card-on-file first if none, then the
   *  package picker) and clears it. Never set for guides. */
  buyIntent: boolean;

  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  setView(v: View): void;
  /** `from` is the caller's auth uuid; `fromName` their wire-carried display name. */
  setIncoming(from: string | null, durationMinutes?: number | null, topic?: string | null, fromName?: string | null): void;
  setCallDuration(n: number | null): void;
  setSessionCountdown(n: number | null): void;
  /** `peer` is the peer's auth uuid. */
  setCallPeer(peer: string | null): void;
  setLoopbackOf(id: string | null): void;
  setSessionMinutes(n: number): void;
  setLastSessionId(id: number | null): void;
  setLastCallPeer(name: string | null): void;
  setGuideReady(v: boolean): void;
  setBuyIntent(v: boolean): void;
  /** Re-load profile + roster from Supabase using the current session. */
  refreshProfile(): Promise<void>;
}

export function roleOf(users: UserSummary[], userId: string | null): UserRole | null {
  if (!userId) return null;
  return users.find((u) => u.id === stripMirror(userId))?.role ?? null;
}

/** Display name for a user id, or null when the roster doesn't know them (they
 *  signed up after this client's last roster load, or they're the captioner).
 *  Callers supply their own human-readable fallback — a uuid must never reach
 *  the screen. */
export function nameOf(users: UserSummary[], userId: string | null): string | null {
  if (!userId) return null;
  return users.find((u) => u.id === stripMirror(userId))?.name ?? null;
}

/** The loopback self-test joins twice, so its mirror identity is the user's
 *  uuid plus this exact literal suffix. Never a regex, never `includes`. */
function stripMirror(userId: string): string {
  return userId.endsWith('-mirror') ? userId.slice(0, -'-mirror'.length) : userId;
}

const initial: Omit<AppState,
  | 'signIn' | 'signOut' | 'setView' | 'setIncoming' | 'setCallDuration' | 'setSessionCountdown' | 'setCallPeer'
  | 'setLoopbackOf' | 'setSessionMinutes' | 'setLastSessionId' | 'setLastCallPeer' | 'setGuideReady' | 'setBuyIntent' | 'refreshProfile'> = {
  view: 'login',
  userId: null,
  displayName: null,
  email: null,
  registrationStatus: null,
  applicationStage: null,
  stageFeedback: null,
  role: null,
  incomingFrom: null,
  incomingFromName: null,
  incomingDurationMinutes: null,
  incomingTopic: null,
  callDurationMinutes: null,
  sessionCountdownSeconds: null,
  callPeer: null,
  loopbackOf: null,
  users: [],
  sessionMinutes: 0,
  lastSessionId: null,
  lastCallPeer: null,
  guideReady: false,
  buyIntent: false,
};

// supabase-js's in-memory auth client gets wedged after tab-visibility changes,
// making signInWithPassword / signOut / getSession / from / rpc hang
// indefinitely even when the underlying HTTP calls succeed. Every operation
// below talks directly to the Supabase REST/Auth endpoints over fetch() and
// reads/writes the session storage key via lib/session.ts. supabase-js is no
// longer in the critical path for any user-driven action; we keep its package
// only for the URL-fragment session detection used by the password-recovery
// flow (see App.tsx's onAuthStateChange listener).
const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL ?? '').replace(/\/+$/, '');
const PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? '';

async function passwordGrant(email: string, password: string): Promise<StoredSession> {
  const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
    method: 'POST',
    headers: { apikey: PUBLISHABLE_KEY, 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const body = await res.json().catch(() => ({} as Record<string, unknown>));
  if (!res.ok) {
    throw new Error(signInErrorMessage(body, res.status));
  }
  return body as StoredSession;
}

async function loadProfileAndRoster(): Promise<{
  userId: string | null;
  displayName: string | null;
  email: string | null;
  registrationStatus: string | null;
  applicationStage: string | null;
  stageFeedback: string | null;
  role: UserRole | null;
  sessionMinutes: number;
  users: UserSummary[];
}> {
  // getValidAccessToken refreshes via refresh_token if the stored token is
  // expired (e.g., on bootstrap after a long absence). Returns null if there
  // is no session, or refresh failed; caller (App.tsx onMount) treats either
  // as "leave the user on LoginView".
  const accessToken = await getValidAccessToken();
  if (!accessToken) {
    return { userId: null, displayName: null, email: null, registrationStatus: null, applicationStage: null, stageFeedback: null, role: null, sessionMinutes: 0, users: [] };
  }
  const authHeaders = {
    apikey: PUBLISHABLE_KEY,
    Authorization: `Bearer ${accessToken}`,
  };
  // get_my_profile() SECURITY DEFINER RPC returns the caller's full row
  // including session_minutes (the direct profiles SELECT is column-
  // restricted for the authenticated role and would 403 on that column).
  const profRes = await fetch(`${SUPABASE_URL}/rest/v1/rpc/get_my_profile`, {
    method: 'POST',
    headers: { ...authHeaders, 'Content-Type': 'application/json' },
    body: '{}',
  });
  if (!profRes.ok) throw new Error(`get_my_profile failed (HTTP ${profRes.status})`);
  const profRows = await profRes.json() as Array<Record<string, unknown>> | Record<string, unknown>;
  const profile = (Array.isArray(profRows) ? profRows[0] : profRows) ?? null;
  if (!profile) throw new Error('profile not found');

  // public_profiles view — lobby roster.
  const rosterRes = await fetch(
    `${SUPABASE_URL}/rest/v1/public_profiles?select=id,display_name,role,rating_count,rating_avg&order=display_name.asc`,
    { headers: authHeaders },
  );
  if (!rosterRes.ok) throw new Error(`public_profiles failed (HTTP ${rosterRes.status})`);
  const roster = await rosterRes.json() as Array<{
    id: string;
    display_name: string;
    role: string;
    rating_count: number;
    rating_avg: number | null;
  }>;

  return {
    // The uuid is the identity. get_my_profile() selects it as auth.uid() of
    // the very token we just used, and the null-profile case already threw
    // above, so this is always present.
    userId: profile.id as string,
    displayName: profile.display_name as string,
    email: (profile.email as string | null) ?? null,
    registrationStatus: (profile.registration_status as string | null) ?? null,
    applicationStage: (profile.application_stage as string | null) ?? null,
    stageFeedback: (profile.stage_feedback as string | null) ?? null,
    role: profile.role as UserRole,
    sessionMinutes: (profile.session_minutes as number) ?? 0,
    users: (roster ?? []).map((r) => ({
      id: r.id,
      name: r.display_name,
      role: r.role as UserRole,
      ratingCount: r.rating_count,
      ratingAvg: r.rating_avg,
    })),
  };
}

export const useAppStore = create<AppState>((set, get) => ({
  ...initial,

  async signIn(email, password) {
    const session = await passwordGrant(email, password);
    writeStoredSession(session);
    await get().refreshProfile();
    set({ view: 'lobby' });
  },

  async signOut() {
    // Local-only sign-out — see the long comment in commit 7afeecc for why we
    // don't call supabase.auth.signOut() (it hangs and races with signIn).
    // Best-effort: tell the backend to remove us from the LiveKit lobby so the
    // admin online count drops within the 5s poll, even if the WebSocket
    // teardown is delayed. Fire-and-forget; failure is tolerated.
    const session = readStoredSession();
    if (session?.access_token) {
      fetch('/api/token/leave', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      }).catch(() => { /* ignore */ });
    }
    clearStoredSession();
    set({ ...initial });
    // Detach analytics so the next person on a shared browser starts fresh.
    resetUser();
  },

  async refreshProfile() {
    const p = await loadProfileAndRoster();
    set({
      userId: p.userId,
      displayName: p.displayName,
      email: p.email,
      registrationStatus: p.registrationStatus,
      applicationStage: p.applicationStage,
      stageFeedback: p.stageFeedback,
      role: p.role,
      sessionMinutes: p.sessionMinutes,
      users: p.users,
    });
    // Tie analytics to the signed-in user. This is the single identity-load
    // chokepoint (sign-in, bootstrap, and email-confirm all route here), so one
    // identify call covers every path. Distinct ID is the stable Supabase auth
    // UUID; the display name rides along as a property.
    if (p.userId) {
      identifyUser(p.userId, { display_name: p.displayName, role: p.role });
    }
  },

  setView(v) { set({ view: v }); },
  setIncoming(from, durationMinutes = null, topic = null, fromName = null) {
    set({
      incomingFrom: from,
      incomingFromName: from === null ? null : fromName,
      incomingDurationMinutes: from === null ? null : durationMinutes,
      incomingTopic: from === null ? null : topic,
    });
  },
  setCallDuration(n) { set({ callDurationMinutes: n }); },
  setSessionCountdown(n) { set({ sessionCountdownSeconds: n }); },
  setCallPeer(peer) { set({ callPeer: peer }); },
  setLoopbackOf(id) { set({ loopbackOf: id }); },
  setSessionMinutes(n) { set({ sessionMinutes: n }); },
  setLastSessionId(id) { set({ lastSessionId: id }); },
  setLastCallPeer(name) { set({ lastCallPeer: name }); },
  setGuideReady(v) { set({ guideReady: v }); },
  setBuyIntent(v) { set({ buyIntent: v }); },
}));

useAppStore.getInitialState = () => ({ ...initial }) as AppState;
